import {
  type PopulationCoverageJurisdiction,
  type PopulationCoverageState,
  stateFipsToAbbr,
} from '@mollusk/shared';
import Box from '@mui/material/Box';
import { useQuery } from '@tanstack/react-query';
import mapboxgl from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/endpoints';
import { LoadingPill } from '../../components/LoadingPill';
import { CoverageHeadline } from './CoverageHeadline';
import { byFeatureId, coverageByStateFips, paintFeatures } from './geo';
import { EMPTY_FC, setSourceData } from './mapUtil';
import { countyLevelFeatures, topoToFeatures } from './topo';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN ?? '';

export function PopulationCoverageMap({
  states,
  jurisdictions,
  recordNoun,
  methodology,
}: {
  states: PopulationCoverageState[];
  jurisdictions: PopulationCoverageJurisdiction[];
  recordNoun: string;
  methodology: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [ready, setReady] = useState(false);
  const statesGeo = useQuery({
    queryKey: ['coverage-geo', 'states'],
    queryFn: () => api.coverageGeo('states'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const jurisGeo = useQuery({
    queryKey: ['coverage-geo', 'permit-jurisdictions'],
    queryFn: () => api.coverageGeo('permit-jurisdictions'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const countiesGeo = useQuery({
    queryKey: ['coverage-geo', 'counties'],
    queryFn: () => api.coverageGeo('counties'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const features = useMemo(
    () => (statesGeo.data ? topoToFeatures(statesGeo.data, 'states') : []),
    [statesGeo.data],
  );
  const statesFC = useMemo(
    () => paintFeatures(features, byFeatureId, coverageByStateFips(states)),
    [features, states],
  );
  const localFC = useMemo<GeoJSON.FeatureCollection>(() => {
    const held = new Map(jurisdictions.filter((j) => j.scope !== 'state').map((j) => [j.geoid, j]));
    const placeFeatures = (jurisGeo.data as GeoJSON.FeatureCollection | undefined)?.features ?? [];
    const countyFeatures = countiesGeo.data ? countyLevelFeatures(countiesGeo.data) : [];
    const geometries = [...placeFeatures, ...countyFeatures];
    const painted: GeoJSON.Feature[] = [];
    for (const f of geometries) {
      const geoid = String(f.properties?.place_geoid ?? f.id ?? '');
      const row = held.get(geoid);
      if (row) {
        painted.push({
          ...f,
          properties: {
            ...f.properties,
            geoid,
            name: row.name,
            scope: row.scope,
            population: row.population,
            record_count: row.record_count,
            coverage: 1,
          },
        });
      }
    }
    return {
      type: 'FeatureCollection',
      features: painted,
    };
  }, [jurisGeo.data, countiesGeo.data, jurisdictions]);
  const totalPopulation = states.reduce((n, s) => n + s.total_population, 0);
  const coveredPopulation = states.reduce((n, s) => n + s.covered_population, 0);
  const records = states.reduce((n, s) => n + s.record_count, 0);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: ref.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-98, 38],
      zoom: 3,
    });
    mapRef.current = map;
    map.on('load', () => {
      map.addSource('population-states', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'population-fill',
        type: 'fill',
        source: 'population-states',
        paint: {
          'fill-color': [
            'interpolate',
            ['linear'],
            ['coalesce', ['get', 'coverage'], 0],
            0,
            '#eceff1',
            0.5,
            '#66c2a4',
            1,
            '#00796b',
          ],
          'fill-opacity': 0.82,
        },
      });
      map.addLayer({
        id: 'population-line',
        type: 'line',
        source: 'population-states',
        paint: { 'line-color': '#607d8b', 'line-width': 0.7 },
      });
      map.addSource('population-local', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'population-local-fill',
        type: 'fill',
        source: 'population-local',
        paint: { 'fill-color': '#004d40', 'fill-opacity': 0.7 },
      });
      setReady(true);
    });
    const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });
    const number = new Intl.NumberFormat('en-US');
    const percent = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });
    map.on('click', 'population-local-fill', (event) => {
      event.originalEvent.preventDefault();
      const p = event.features?.[0]?.properties;
      if (!p) return;
      popup
        .setLngLat(event.lngLat)
        .setHTML(
          `<strong>${escapeHtml(String(p.name ?? 'Covered jurisdiction'))}</strong><br>` +
            `${escapeHtml(scopeLabel(String(p.scope ?? '')))}<br>` +
            `${number.format(Number(p.record_count ?? 0))} records<br>` +
            `${number.format(Number(p.population ?? 0))} people covered`,
        )
        .addTo(map);
    });
    map.on('click', 'population-fill', (event) => {
      if (event.originalEvent.defaultPrevented) return;
      const fips = String(event.features?.[0]?.id ?? '');
      const state = states.find((s) => s.state === stateFipsToAbbr(fips));
      if (!state) return;
      popup
        .setLngLat(event.lngLat)
        .setHTML(
          `<strong>${escapeHtml(state.state)} coverage</strong><br>` +
            `${percent.format(state.coverage)} of residents<br>` +
            `${number.format(state.record_count)} records<br>` +
            `${number.format(state.covered_jurisdictions)} covered jurisdictions`,
        )
        .addTo(map);
    });
    for (const layer of ['population-fill', 'population-local-fill']) {
      map.on('mouseenter', layer, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layer, () => {
        map.getCanvas().style.cursor = '';
      });
    }
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [states]);
  useEffect(() => {
    const map = mapRef.current;
    if (ready && map && statesFC) setSourceData(map, 'population-states', statesFC);
  }, [ready, statesFC]);
  useEffect(() => {
    const map = mapRef.current;
    if (ready && map) setSourceData(map, 'population-local', localFC);
  }, [ready, localFC]);

  return (
    <>
      <CoverageHeadline
        national={{
          coverage: totalPopulation ? coveredPopulation / totalPopulation : 0,
          covered_weight: coveredPopulation,
          total_weight: totalPopulation,
          record_count: records,
        }}
        weightLabel="population-weighted"
        recordNoun={recordNoun}
        methodology={methodology}
      />
      {!statesGeo.data ? <LoadingPill label="Loading coverage map" /> : null}
      <Box ref={ref} sx={{ height: 500, borderRadius: 1, overflow: 'hidden', mb: 2 }} />
    </>
  );
}

const scopeLabel = (scope: string) =>
  scope === 'county_balance' ? 'County / unincorporated coverage' : 'Place coverage';
const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
